import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useQuery } from '@tanstack/react-query';
import { generateSitemap, downloadSitemap } from '@/utils/sitemapGenerator';
import { supabase } from '@/integrations/supabase/client';
import { Download, ExternalLink, Globe, RefreshCw, FileText } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

const AdminSitemapTab: React.FC = () => {
  const { toast } = useToast();
  const [isGenerating, setIsGenerating] = useState(false);

  const { data: sitemapData, isLoading, refetch } = useQuery({
    queryKey: ['sitemap-preview'],
    queryFn: generateSitemap,
    staleTime: 1000 * 60 * 5, // 5 minutes
  });

  const { data: locationCount } = useQuery({
    queryKey: ['location-count'],
    queryFn: async () => {
      const { count } = await supabase
        .from('locations')
        .select('*', { count: 'exact', head: true })
        .eq('is_published', true)
        .eq('is_active', true);
      return count || 0;
    },
  });

  const handleDownloadSitemap = async () => {
    setIsGenerating(true);
    try {
      await downloadSitemap();
      toast({
        title: "Sitemap Downloaded",
        description: "The sitemap.xml file has been downloaded successfully. Replace the public/sitemap.xml file with this content.",
      });
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to download sitemap. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsGenerating(false);
    }
  };

  const handleRefreshSitemap = () => {
    refetch();
    toast({
      title: "Sitemap Refreshed",
      description: "The sitemap preview has been updated.",
    });
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to Clipboard",
      description: "The sitemap URL has been copied to your clipboard.",
    });
  };

  // Count URLs in sitemap
  const urlCount = sitemapData ? (sitemapData.match(/<url>/g) || []).length : 0;

  const sitemapUrl = "https://itmgmtsol.com/sitemap.xml";

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Globe className="h-5 w-5" />
            Sitemap Management
          </CardTitle>
          <CardDescription>
            Manage your website's static sitemap for search engine optimization. The sitemap is currently served as a static file and includes all published location pages.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-blue-50 p-4 rounded-lg">
              <h4 className="font-semibold text-blue-900">Total URLs in Static File</h4>
              <p className="text-2xl font-bold text-blue-700">113</p>
              <p className="text-sm text-blue-600">Including all pages and locations</p>
            </div>
            <div className="bg-green-50 p-4 rounded-lg">
              <h4 className="font-semibold text-green-900">Published Locations</h4>
              <p className="text-2xl font-bold text-green-700">{locationCount}</p>
              <p className="text-sm text-green-600">Active location pages</p>
            </div>
            <div className="bg-purple-50 p-4 rounded-lg">
              <h4 className="font-semibold text-purple-900">Sitemap Status</h4>
              <p className="text-lg font-bold text-purple-700">Static</p>
              <p className="text-sm text-purple-600">Manual updates</p>
            </div>
          </div>

          <div className="border rounded-lg p-4 bg-gray-50">
            <h4 className="font-semibold mb-2">Sitemap URL</h4>
            <div className="flex items-center gap-2">
              <code className="flex-1 bg-white px-3 py-2 rounded border text-sm">
                {sitemapUrl}
              </code>
              <Button
                variant="outline"
                size="sm"
                onClick={() => copyToClipboard(sitemapUrl)}
              >
                Copy
              </Button>
              <Button
                variant="outline"
                size="sm"
                asChild
              >
                <a href={sitemapUrl} target="_blank" rel="noopener noreferrer">
                  <ExternalLink className="h-4 w-4" />
                </a>
              </Button>
            </div>
          </div>

          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <FileText className="h-5 w-5 text-yellow-600 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-900 mb-2">Manual Update Process</h4>
                <div className="text-sm text-yellow-800 space-y-1">
                  <p>1. Generate an updated sitemap using the button below</p>
                  <p>2. Download the generated XML file</p>
                  <p>3. Replace the content in <code className="bg-yellow-100 px-1 rounded">public/sitemap.xml</code></p>
                  <p>4. Deploy the changes to make them live</p>
                </div>
              </div>
            </div>
          </div>

          <div className="flex gap-2">
            <Button onClick={handleDownloadSitemap} disabled={isGenerating}>
              <Download className="h-4 w-4 mr-2" />
              {isGenerating ? 'Generating...' : 'Generate & Download Updated Sitemap'}
            </Button>
            <Button variant="outline" onClick={handleRefreshSitemap} disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? 'animate-spin' : ''}`} />
              Refresh Preview
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Google Search Console Submission</CardTitle>
          <CardDescription>
            Submit your sitemap to Google Search Console to improve search engine indexing.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <p className="text-sm text-gray-600">
              1. Go to <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Google Search Console</a>
            </p>
            <p className="text-sm text-gray-600">
              2. Select your property (itmgmtsol.com)
            </p>
            <p className="text-sm text-gray-600">
              3. Go to Sitemaps in the left sidebar
            </p>
            <p className="text-sm text-gray-600">
              4. Add the sitemap URL: <code className="bg-gray-100 px-2 py-1 rounded">sitemap.xml</code>
            </p>
            <Button variant="outline" asChild>
              <a href="https://search.google.com/search-console" target="_blank" rel="noopener noreferrer">
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Search Console
              </a>
            </Button>
          </div>
        </CardContent>
      </Card>

      {sitemapData && (
        <Card>
          <CardHeader>
            <CardTitle>Current Database-Generated Sitemap Preview</CardTitle>
            <CardDescription>
              Preview of what an updated sitemap would contain based on current database. Use this to generate a new static file when locations change.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="mb-4">
              <p className="text-sm text-gray-600">
                URLs in preview: <span className="font-semibold">{urlCount}</span> | 
                Published locations: <span className="font-semibold">{locationCount}</span>
              </p>
            </div>
            <pre className="bg-gray-100 p-4 rounded-lg text-xs overflow-auto max-h-96">
              {sitemapData}
            </pre>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AdminSitemapTab;
